<template>
  <div class="form-card">
    <div class="form-card__inner">
      <div class="form-card__header">
        <div class="form-card__icon">
          <img
            src="@/assets/icons/phone.svg"
            alt="phone"
          >
        </div>

        <h3 class="form-card__title title title--secondary">
          {{$t('refill.title')}}
        </h3>

        <div class="form-card__subtitle">
          {{$t('refill.subtitle')}}
        </div>
      </div>

      <div class="form-card__input">
        <ap-input-tel
          v-model="sender.phone"
          :label="t('rechargeable_number')"
          :icon-name="icon"
          :error-text="refillError.phone"
          v-on="!filledPhone ? { keyup: clearPhoneError } : {}"
        />
      </div>

      <div class="form-card__input">
        <ap-input-money
          v-model="sender.amount"
          type="sum"
          :label="t('amount')"
          currency
          :error-text="refillError.amount"
          @keyup="clearPhoneError"
        />
      </div>

      <div class="form-card__hint">{{$t('refill.hint')}}</div>
        
      <div class="form-card__btn">
        <ap-button
          type="primary"
          full
          :disable="disableBtn"
          @click="validateSender"
        >
          <div class="button__loader">
            <div
              v-if="checking"
              class="spinner"
            />
            <div
              v-else
              class="button__text"
            >
              {{ $t('continue')}}
            </div>
          </div>
        </ap-button>
      </div>
    </div>  
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { Sender } from '@/util/interface';
import { PHONE_LENGTH, SIM, STATUS_OK } from '@/constant';


import apButton from './UI/apButton.vue';
import apInput from './UI/apInput.vue';
import ApInputMask from './UI/apInputMask.vue';
import ApInputMoney from './UI/apInputMoney.vue';
import apInputTel from './UI/apInputTel.vue';

export default defineComponent({
  components: {
    apInputTel,
    apInput,
    apButton,
    ApInputMask,
    ApInputMoney
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { t } = useI18n();
    
    const sender = ref<Sender>({
      phone: '',
      amount: ''
    });

    const checking = ref(false);

    const filledPhone = computed(() => {
      const filled =  sender.value.phone.length === PHONE_LENGTH;

      filled 
        ? store.dispatch('checkOperator', { phone: `7${sender.value.phone}`,
          type: SIM })
        : store.commit('setOperator', {type: SIM});

      return filled;
    });

    const language = computed(() => store.getters['language']);
    const refillError = computed(() => {
      const errors = {
        phone: '',
        amount: ''
      };
      const { errors: cardError } = store.getters['simRemittances/remittancesFromCardErrors'];
      const { errors: simError } = store.getters['simRemittances/remittancesFromSimErrors'];
      if (cardError && simError) {
        const phone = (cardError?.phone?.[0] ? cardError?.phone?.[0] + '\n' : '') + (simError?.phone?.[0] || '');
        const amount = (cardError?.amount?.[0] ? cardError?.amount?.[0] + '\n' : '') + (simError?.amount?.[0] || '');
        errors.phone = phone;
        errors.amount = amount;
      }
      return errors;
    });
    
    const icon = computed(() => {
      const data = store.getters['refillOperator'];
      const { operator } = data;

      if (operator) {
        return operator;
      }
      return '';
    });
    

    const disableBtn = computed(() => !(sender.value.phone.length === 10 && +sender.value.amount > 0));
    
    const clearPhoneError = (): void => {
      if (refillError.value.phone.length || refillError.value.amount.length) {
        store.commit('simRemittances/setRemittancesFromCardErrors', {});
        store.commit('simRemittances/setRemittancesFromSimErrors', {});
      }
    };

    const validateSender = async () => {
      checking.value = true;
      const amount = sender.value.amount.replaceAll(',','').replaceAll(/\s/g, '');
      const data = {
        amount,
        phone: '7' + sender.value.phone,
        language: language.value
        
      };
      const infoFromCard = await store.dispatch('simRemittances/prevalidateRemittancesFromCard', data);
      const infoFromSim = await store.dispatch('simRemittances/prevalidateRemittancesFromSim', {
        description: 'Перевод с баланса на баланс',
        type: SIM,
        amount,
        recipient: '7' + sender.value.phone,
        language: language.value
      });


      const isPossiblePayment = (infoFromCard.status && infoFromCard.status === STATUS_OK) 
                                || (infoFromSim.status && infoFromSim.status === STATUS_OK); 
      if (isPossiblePayment) {
        router.push({ name: 'sim-remittances' });      
      }
      checking.value = false;
    };

    onBeforeUnmount(() => {
      store.commit('clearOperator');
    });
    
    return {
      sender,
      disableBtn,
      validateSender,
      icon,
      refillError,
      checking,
      clearPhoneError,
      filledPhone,
      t
    };
  },
});
</script>
<style scoped lang="scss">
.form-card{
  background-color: $primary-white;
  border-radius: 24px;
  height: 100%;
}
.default{
  .form-card{
    padding: 34px 40px 58px 40px;

    &__icon{
      margin-bottom: 7px;
    }
    &__title{
      font-style: normal;
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 8px;
    }
    &__subtitle{
      font-weight: 400;
      font-size: 16px;
      line-height: 140%;
    }
    &__header{
      margin-bottom: 38px;
    }
    &__input:first-child{
      margin-bottom: 22px;
    }
    &__input{
      margin-bottom: 24px;
    }
    &__hint{
      color: rgba(14, 36, 58, 0.5);
      font-size: 14px;
      margin-bottom: 20px;
    }
  }
}
.tele2, .altel{
  .form-card{
    padding: 40px;
    background-color: #ffffff;
    &__inner{
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    &__icon{
      display: none;
    }
    &__title{
      font-weight: bold;
    }
    &__subtitle{
      color: $primary-gray;
    }
    &__input:first-child {
      margin-bottom: 24px;
    }
    &__input{
      margin-bottom: 20px;
    }
    &__hint{
      margin-bottom: 20px;
    }
    &__btn{
      margin: 0;
    }
  }
}
.tele2{
  .form-card{
    &__title{
      font-style: normal;
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 8px;
      color: $tele2-primary-black;
    }
    &__subtitle{
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      font-family: 'Open Sans';
      color: $tele2-primary-gray;
    }
    &__header{
      margin-bottom: 24px;
    }
    &__hint{
      font-family: "Open sans";
      font-weight: 600;
      font-size: 13px;
      line-height: 21px;
      color: #646468;
    }
  }
}
.altel{
  .form-card{
    &__title{
      font-weight: 700;
      font-size: 40px;
      line-height: 48px;
      margin-bottom: 6px;
      color: $altel-primary-black;
    }
    &__subtitle{
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      color: $altel-primary-gray;

    }
    &__header{
      margin-bottom: 28px;
    }
    &__hint{
      color: rgba(14, 36, 58, 0.5);
      font-size: 14px;
    }
  }  
}
@media (min-width: 320px) and (max-width: 1024px) {
  .tele2, .altel, .default{
    .form-card{
      &__title{
        font-weight: 700;
        font-size: 24px;
        line-height: 34px;
      }
    }
  }
  .default{
    .form-card{
      padding: 21px 16px 16px 16px;
    }
  }
  .tele2, .altel{
    .form-card{
      padding: 40px 16px;
    }
  }
}
</style>
